import { useEffect, useState } from "react";
import { DeleteImpact } from "@/types";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";

interface UseDeleteImpactProps {
  documentId: string | undefined;
  /** Only fire when the dialog is open — the preview is per-open. */
  enabled: boolean;
}

interface UseDeleteImpactReturn {
  data: DeleteImpact | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches the read-only delete-impact preview from
 * POST /documents/{id}/delete-impact (#5751, ADR-078).
 *
 * Calls httpClient directly rather than dataProvider — the endpoint returns a
 * three-set impact payload, not an identified record, so the dataProvider
 * contract doesn't fit. httpClient still carries auth + partner headers and
 * resolves `{ json }` (the parsed body). Mirrors `useReviewProjection`.
 *
 * The endpoint is non-destructive; re-fetching on each open is safe.
 */
export const useDeleteImpact = ({ documentId, enabled }: UseDeleteImpactProps): UseDeleteImpactReturn => {
  const [data, setData] = useState<DeleteImpact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !documentId) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/delete-impact`;
    httpClient(url, { method: "POST", body: "{}" })
      .then(({ json }: { json: unknown }) => {
        if (cancelled) return;
        const payload = json as Partial<DeleteImpact> | undefined;
        if (
          !payload ||
          !Array.isArray(payload.delete) ||
          !Array.isArray(payload.keep_and_scrub) ||
          !Array.isArray(payload.blocked)
        ) {
          // Log only the top-level keys, never values — DeleteEntry.label carries
          // claimant names / policy numbers (PHI) that must not reach the console.
          console.error(
            "Delete-impact endpoint returned an unexpected response shape. " +
              "Is the mgmt-api running with the /delete-impact route registered (#5749)?",
            payload && typeof payload === "object" ? Object.keys(payload) : typeof json
          );
          setError("Delete-impact endpoint returned an unexpected response shape.");
          setData(null);
          return;
        }
        setData(payload as DeleteImpact);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Log a scrubbed descriptor, not the raw error — an error body can echo
        // DeleteEntry.label values (claimant names / policy numbers = PHI).
        const status =
          typeof err === "object" && err !== null && "status" in err ? (err as { status?: unknown }).status : undefined;
        console.error("Delete-impact preview failed", status !== undefined ? `(status ${status})` : "");
        setError(err instanceof Error ? err.message : "Failed to load delete impact");
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, enabled]);

  return { data, isLoading, error };
};
